import React, { Dispatch, FC, SetStateAction } from "react";
import { isBefore } from "date-fns";
import _camelCase from "lodash/camelCase";
import { StickyCTABar } from "component-library";
import { useTranslation } from "i18n";
import { ISelfTapeRequestStatus, MediaRequestType } from "shared-types";
import { navigator } from "shared/services/navigator";
import applicationMap from "services/applicationMap";
import { ISelfTapeRequest } from "shared/models/selfTapeRequest";
import { getCancelButton } from "../helpers";
import { AgentActionFooter } from "../SelfTapeRequestAgentParts";

interface Props {
  selfTapeRequest: ISelfTapeRequest;
  clientName: string;
  setAcceptRequestModalIsOpen: Dispatch<SetStateAction<boolean>>;
  isAgent?: boolean;
}

const SelfTapeRequestActionFooter: FC<React.PropsWithChildren<Props>> = ({
  selfTapeRequest,
  clientName,
  setAcceptRequestModalIsOpen,
  isAgent,
}) => {
  const { t } = useTranslation();

  const isRejected = selfTapeRequest.status === ISelfTapeRequestStatus.Rejected;
  const isExpired = isBefore(new Date(selfTapeRequest.expiryDate), new Date());

  const ctaHidden =
    isExpired || isRejected || Boolean(selfTapeRequest.submissionMedia.length);

  const isSubmitDisabled = selfTapeRequest.isPublished;

  const getSubmitButtonConfig = () => {
    switch (selfTapeRequest.status) {
      case ISelfTapeRequestStatus.Awaiting:
        return {
          text: t("common:selfTapes.request.button.responseToRequest"),
          onClick: () => setAcceptRequestModalIsOpen(true),
          disabled: isSubmitDisabled,
        };
      case ISelfTapeRequestStatus.Accepted:
        return {
          text: t(
            `common:selfTapes.request.submit.${_camelCase(
              MediaRequestType[selfTapeRequest.mediaRequestType]
            )}`
          ),
          onClick: () =>
            navigator.goTo(
              applicationMap.links.selfTapeUpload(
                selfTapeRequest.performerRequestId
              )
            ),
          disabled: isSubmitDisabled,
        };
      default:
        return {
          disabled: isSubmitDisabled,
          text: t("common:selfTapes.request.button.submit"),
          onClick: () =>
            navigator.goTo(
              applicationMap.links.selfTapeUpload(
                selfTapeRequest.performerRequestId
              )
            ),
        };
    }
  };

  if (ctaHidden) return null;

  const submitButtonConfig = getSubmitButtonConfig();

  return (
    <>
      {isAgent ? (
        <AgentActionFooter
          selfTapeRequestId={selfTapeRequest.performerRequestId}
          clientName={clientName}
          submitButtonConfig={submitButtonConfig}
          isAccepted={
            selfTapeRequest.status === ISelfTapeRequestStatus.Accepted
          }
          isTagminUser={selfTapeRequest.isTagminUser}
          isTagTapesUser={selfTapeRequest.isTagTapesUser}
          forwardedToPerformer={selfTapeRequest.forwardedToPerformer}
        />
      ) : (
        <StickyCTABar
          prev={getCancelButton(t, t("common:button.label.back"))}
          next={submitButtonConfig}
          ctaBarAdditionalClassName="max-w-[1440px]"
          isSticky
        />
      )}
    </>
  );
};

export default SelfTapeRequestActionFooter;
